import { useQuery } from '@tanstack/react-query'
import {
  referenceApi,
  type SalesRep,
  type PricingTemplate,
  type AssignableUser,
  type MccOption,
  type AgmsMccBucket,
} from '../lib/api'

/**
 * Hook for fetching sales reps for dropdowns.
 * Data is cached and rarely changes.
 */
export function useSalesReps() {
  return useQuery({
    queryKey: ['salesReps'],
    queryFn: referenceApi.salesReps,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  })
}

/**
 * Hook for fetching pricing templates for dropdowns.
 * Data is cached and rarely changes.
 */
export function usePricingTemplates() {
  return useQuery({
    queryKey: ['pricingTemplates'],
    queryFn: referenceApi.pricingTemplates,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  })
}

/**
 * Hook for fetching users eligible for lead assignment.
 * Powers inline assignee dropdowns on the lead detail page.
 */
export function useAssignableUsers() {
  return useQuery({
    queryKey: ['assignableUsers'],
    queryFn: referenceApi.assignableUsers,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  })
}

/**
 * Hook for fetching the AGMS MCC industry options for TurboApp Step 3.
 * AD-013 Slice 3-Lite §2.4. Cached aggressively — the list rarely
 * changes (config/agms_mcc_options.php is hand-edited).
 */
export function useMccOptions() {
  return useQuery({
    queryKey: ['mccOptions'],
    queryFn: referenceApi.mccOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  })
}

export type { SalesRep, PricingTemplate, AssignableUser, MccOption, AgmsMccBucket }
